import { BilledServiceItem, SalesInboxItem, TransactionsInboxItem } from "@/Billing/Api/Inbox/Dto/Response/BillingInboxItem";
import { SalesInboxTableItem, TransactionsInboxTableItem } from "@/Billing/Models/UI/BillingInboxTableItem";
import { BillingStore } from "@/Billing/Stores/BillingStore";
import { BillingPaymentMethodsHelper } from "@/Common/Helpers/BillingPaymentMethodsHelper";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { Locale } from "@/Common/Models/Localization/Locale";
import { Patient } from "@/Common/Models/Patient/Patient";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";

class BillingResponseHandler extends BaseServerOperationHandler {
    constructor(action: BaseAction) {
        super(action);

        this.RegisterOperation(ServerOperations.Server.Myle_LoadTransactionsData, this.LoadTransactionsData);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadSalesData, this.LoadSalesData);
        this.RegisterOperation(ServerOperations.Server.Myle_GetBillingSelectedData, this.OpenSelectedBill);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadPatient, this.LoadPatientInfo, SubServerOperations.Billing.LoadPatientTransaction);
        this.RegisterOperation(ServerOperations.Server.Myle_Billing_GenerateReportPDF, this.PrintReport);
        this.RegisterOperation(ServerOperations.Server.Myle_Billing_GeneratePayableBillsReportPdf, this.PrintReport);
    }

    private LoadPatientInfo(response: ServerEvent, store: BillingStore): void {
        const patient = Patient.FromResponse(response);

        store.BillingPopupModel = store.BillingPopupModel
            .set("PatientName", patient.GetFormattedName())
            .set("PatientNAM", patient.GetFormattedNAM())
            .set("PatientNAMExpiryDate", patient.GetFormattedNAMExpirationDateString())
            .set("PatientDateOfBirth", patient.GetFormattedDateOfBirth())
            .set("PatientPhone", patient.GetPhoneRaw(0))
            .set("PatientAddress", patient.getFullFormattedAddress());

        store.TransactionsInboxModel = store.TransactionsInboxModel.set("BillingPopupIsVisible", true);

        sessionStore.showGlobalOverlay();
    }

    private OpenSelectedBill(response: ServerEvent, store: BillingStore): void {
        var TableItems = response.Data.BilledService.map(n => {
            var itemModel: BilledServiceItem = new BilledServiceItem();

            itemModel.ServiceMultiplicator = n.Multiplicator;
            itemModel.CostCenterId = n.CostCenterId;
            itemModel.HasGST = n.HadGST;
            itemModel.HasQST = n.HadQST;
            itemModel.HasHST = n.HadHST;
            itemModel.HasGenericTax = n.HadGenericTax;

            itemModel.ServiceName = itemModel.LoadServiceName(n.NameEs, n.NameFr, n.NameEs);

            if (n.ServiceNumber)
                itemModel.ServiceNumber = n.ServiceNumber;
            else
                itemModel.ServiceNumber = "N/A";

            itemModel.ServicePrice = n.Price;

            return itemModel;
        });
        store.BillingPopupModel = store.BillingPopupModel.set("BilledService", TableItems);

        var patientId = store.BillingPopupModel.get("PatientId");
        store.BillingAction.LoadPatient(patientId, SubServerOperations.Billing.LoadPatientTransaction);
    }

    private LoadTransactionsData(response: ServerEvent, store: BillingStore): void {
        var TableItems = response.Data.Results.map(n => {
            var itemModel: TransactionsInboxItem = new TransactionsInboxItem();

            itemModel.PrivateBillId = n.PrivateBillId;
            itemModel.PatientId = n.PatientId;
            itemModel.PatientContactId = n.PatientContactId;

            var employeeProvider = cacheStore.GetEmployeeByContactFacetId(n.EmployeeId);

            var employeeTitleProvider = `${cacheStore.GetLocalizedValueByLookupId(employeeProvider?.PrefixLookupId)} `;

            itemModel.TransactionProvider = employeeProvider
                ? `${employeeTitleProvider}${employeeProvider.LastName}, ${employeeProvider.FirstName}(${employeeProvider.FirstLicenceNumber})`
                : "--";

            var employeeBilledBy = cacheStore.GetEmployeeByContactFacetId(n.BilledBy);
            var employeeTitleBilledBy = cacheStore.GetLocalizedValueByLookupId(employeeBilledBy.PrefixLookupId);

            itemModel.BilledBy = employeeTitleBilledBy + " " + employeeBilledBy.LastName + ", " + employeeBilledBy.FirstName + "(" + employeeBilledBy.FirstLicenceNumber + ")";
            itemModel.BilledTo = n.BilledTo;
            itemModel.RecipientName = n.RecipientName;

            itemModel.PatientName = NameHelper.formatFullNameForHasPatientName(n);
            itemModel.patientNAM = n.PatientNAM;

            itemModel.TransactionNumber = n.Number;
            itemModel.TransactionDate = n.BillDate;
            itemModel.VisitDate = n.VisitDate;
            itemModel.TotalBill = Math.round((n.TotalBill + 0.00001) * 100) / 100;
            itemModel.SubTotal = Math.round((n.Subtotal + 0.00001) * 100) / 100;
            itemModel.GST = Math.round((n.TotalGst + 0.00001) * 100) / 100;
            itemModel.QST = Math.round((n.TotalQst + 0.00001) * 100) / 100;
            itemModel.HST = Math.round((n.TotalHst + 0.00001) * 100) / 100;
            itemModel.GenericTax = Math.round((n.TotalGenericTax + 0.00001) * 100) / 100;
            itemModel.SiteGST = n.GST;
            itemModel.SiteQST = n.QST;
            itemModel.SiteHST = n.HST;
            itemModel.SiteGenericTax = n.GenericTax;
            itemModel.BillNote = n.BillNote;
            itemModel.PaymentMethod = BillingPaymentMethodsHelper.getPaymentMethodLabelWithId(n.PayementType);
            itemModel.PaymentDate = n.PaymentDate;

            switch (n.DiscountType) {
                case 0:
                    itemModel.DiscountType = "$";
                    break;

                case 1:
                    itemModel.DiscountType = "%";
                    break;

                default:
                    break;
            }

            if (n.DiscountType === 0) {
                // Discount --> $
                itemModel.Discount_Dollars = n.Discount;
            }
            else if (n.DiscountType === 1) {
                // Discount --> %
                itemModel.Discount_Dollars = n.Subtotal * (n.Discount / 100);
                itemModel.Discount_Percentage = n.Discount;
            }

            itemModel.IsInvalid = n.IsInvalid;
            itemModel.SiteId = n.SiteId;

            var site = cacheStore.GetSiteById(n.SiteId);
            itemModel.SiteName = site.Model.ClinicalName;

            var tableItem: TransactionsInboxTableItem = new TransactionsInboxTableItem(itemModel);
            return tableItem;
        });

        store.TransactionsInboxModel = store.TransactionsInboxModel.set("TableData", Immutable.List<TransactionsInboxTableItem>(TableItems));
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("TotalQueryPages", response.Data.TotalQueryPages);
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("TotalQueryRows", response.Data.TotalQueryRows);
        const pageSize = store.TransactionsInboxModel.get("SelectedPageSizeOption");
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("CurrentPage", Math.floor(response.Data.Offset / pageSize) + 1);

        store.TransactionsInboxModel = store.TransactionsInboxModel.set("TotalBillSum", Math.round((response.Data.TotalBillSum + 0.00001) * 100) / 100);
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("SubtotalSum", Math.round((response.Data.SubtotalSum + 0.00001) * 100) / 100);
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("GSTSum", Math.round((response.Data.GSTSum + 0.00001) * 100) / 100);
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("QSTSum", Math.round((response.Data.QSTSum + 0.00001) * 100) / 100);
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("HSTSum", Math.round((response.Data.HSTSum + 0.00001) * 100) / 100);
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("GenericTaxSum", Math.round((response.Data.GenericTaxSum + 0.00001) * 100) / 100);
        store.TransactionsInboxModel = store.TransactionsInboxModel.set("DiscountSum", Math.round((response.Data.DiscountSum + 0.00001) * 100) / 100);
    }

    private LoadSalesData(response: ServerEvent, store: BillingStore): void {
        var TableItems = response.Data.Results.map(n => {
            var itemModel: SalesInboxItem = new SalesInboxItem();

            var employeeProvider = cacheStore.GetEmployeeByContactFacetId(n.SaleProvider);

            if (employeeProvider) {
                var employeeTitleProvider = cacheStore.GetLocalizedValueByLookupId(employeeProvider.PrefixLookupId);

                itemModel.SaleProvider = employeeTitleProvider + " " + employeeProvider.LastName + ", " + employeeProvider.FirstName + "(" + employeeProvider.FirstLicenceNumber + ")";
            }

            var employeeBilledBy = cacheStore.GetEmployeeByContactFacetId(n.BilledBy);
            var employeeTitleBilledBy = cacheStore.GetLocalizedValueByLookupId(employeeBilledBy.PrefixLookupId);

            itemModel.BilledBy = employeeTitleBilledBy + " " + employeeBilledBy.LastName + ", " + employeeBilledBy.FirstName + "(" + employeeBilledBy.FirstLicenceNumber + ")";
            itemModel.BilledTo = n.BilledTo;
            itemModel.RecipientName = n.RecipientName;

            itemModel.PatientName = NameHelper.formatFullNameForHasPatientName(n);
            itemModel.patientNAM = n.PatientNAM;
            itemModel.PatientContactId = n.PatientContactId;
            itemModel.SaleNumber = n.SaleNumber;
            itemModel.SaleDate = n.BillDate;
            itemModel.VisitDate = n.VisitDate;
            itemModel.Price = n.Price;
            itemModel.ServiceID = n.ServiceID;
            itemModel.IsInvalid = n.IsInvalid;
            itemModel.Total = Math.round((n.TotalBill + 0.00001) * 100) / 100;
            itemModel.GST = Math.round((n.GST + 0.00001) * 100) / 100;
            itemModel.QST = Math.round((n.QST + 0.00001) * 100) / 100;
            itemModel.HST = Math.round((n.HST + 0.00001) * 100) / 100;
            itemModel.GenericTax = Math.round((n.GenericTax + 0.00001) * 100) / 100;

            itemModel.Quantity = n.Quantity;

            switch (sessionStore.locale) {
                case Locale.fr:
                    itemModel.Description = n.DescriptionFR;
                    break;
                case Locale.es:
                    itemModel.Description = n.DescriptionES;
                    break;
                default:
                    itemModel.Description = n.DescriptionEN;
            }

            itemModel.PaymentMethod = BillingPaymentMethodsHelper.getPaymentMethodLabelWithId(n.PaymentMethod.toString());
            itemModel.IsInvalid = n.IsInvalid;

            var tableItem: SalesInboxTableItem = new SalesInboxTableItem(itemModel);
            return tableItem;
        });

        store.SalesInboxModel = store.SalesInboxModel.set("TableData", Immutable.List<SalesInboxTableItem>(TableItems));
        store.SalesInboxModel = store.SalesInboxModel.set("TotalQueryPages", response.Data.TotalQueryPages);
        store.SalesInboxModel = store.SalesInboxModel.set("TotalQueryRows", response.Data.TotalQueryRows);
        const pageSize = store.SalesInboxModel.get("SelectedPageSizeOption");
        store.SalesInboxModel = store.SalesInboxModel.set("CurrentPage", Math.floor(response.Data.Offset / pageSize) + 1);

        store.SalesInboxModel = store.SalesInboxModel.set("TotalBillSum", Math.round((response.Data.TotalBillSum + 0.00001) * 100) / 100);
        store.SalesInboxModel = store.SalesInboxModel.set("SubtotalSum", Math.round((response.Data.SubtotalSum + 0.00001) * 100) / 100);
        store.SalesInboxModel = store.SalesInboxModel.set("GSTSum", Math.round((response.Data.GSTSum + 0.00001) * 100) / 100);
        store.SalesInboxModel = store.SalesInboxModel.set("QSTSum", Math.round((response.Data.QSTSum + 0.00001) * 100) / 100);
        store.SalesInboxModel = store.SalesInboxModel.set("HSTSum", Math.round((response.Data.HSTSum + 0.00001) * 100) / 100);
        store.SalesInboxModel = store.SalesInboxModel.set("GenericTaxSum", Math.round((response.Data.GenericTaxSum + 0.00001) * 100) / 100);
        store.SalesInboxModel = store.SalesInboxModel.set("DiscountSum", Math.round((response.Data.DiscountSum + 0.00001) * 100) / 100);
    }

    private PrintReport(response: ServerEvent): void {
        if (response?.hasErrors()) {
            sessionStore.showPopup(response.Errors.map(p => cacheStore.GetString(p.Code)));
        }
        else {
            PrintHelper.ShowPrintPreviewFileName(response.Data.FileName);
        }
    }
}

export { BillingResponseHandler };
